/**
 * x-product-form directive.
 *
 * Manages product form submission and variant state. Listens for variant picker
 * events to update the active variant ID and morph the relevant product section
 * HTML. On form submission, collects line item properties and quantity, then
 * adds the product to the cart via `$ajaxCart`.
 *
 * Dispatches `adding-to-cart` before the cart request to allow cross-sell items
 * and other components to inject additional line items into the payload.
 *
 * @options
 *   variantId      - The initially selected variant ID (default: null).
 *   morphSelectors - Additional selectors passed to `$morph` to constrain which
 *                    child elements are updated on variant change (default: []).
 *
 * @events dispatched
 *   product-variant-selected - Fired when a variant option is selected.
 *   product-variant-updated  - Fired after variant HTML has been loaded, with
 *                              `{ variant, html }`.
 *   product-section-morphed  - Fired after the product section has been morphed.
 *   adding-to-cart           - Fired before the cart add request, with
 *                              `{ items, elements, quantity }`. Allows other
 *                              components to push additional items.
 *
 * @example
 *   <div x-product-form="{ variantId: 12345 }" data-section-id="product-main">
 *     …variant picker, add to cart button…
 *   </div>
 */
(() => {
  const root = (Alpine, el, { variantId = null, morphSelectors = [] }) => {
    Alpine.bind(el, () => {
      let timeout;

      return {
        /**
         * Disables the submit button and sets the loading state while the
         * variant data is being fetched.
         */
        ["@variant-picker-selected.stop"]() {
          this.onVariantSelected();

          this.$dispatch("product-variant-selected");
        },

        /**
         * Updates the variant ID and morphs the product section HTML when
         * the variant picker resolves updated HTML.
         */
        ["@variant-picker-updated.stop"](event) {
          const { variant, html } = event.detail;

          this.onVariantUpdated({ variant });

          this.$dispatch("product-variant-updated", {
            variant,
            html,
          });

          const sectionId = this.$buildSectionSelector(
            this.$el.dataset.sectionId,
          );

          const existingSection = document.getElementById(sectionId);
          const newSection = html.getElementById(sectionId);

          if (!(existingSection && newSection)) return;

          this.$morph(existingSection, newSection, { morphSelectors });

          this.$nextTick(() => {
            this.$dispatch("product-section-morphed");
          });
        },

        /** Intercepts the native form submit to handle cart addition via AJAX. */
        ["@submit.prevent.stop"](event) {
          this.onSubmit();
        },

        ["x-data"]() {
          return {
            isLoading: false,
            isUpdating: false,
            isSubmitting: false,
            variantId: variantId,

            /**
             * Called when a variant option is selected but before the variant
             * HTML has loaded. Disables the submit button and sets `isLoading`.
             */
            onVariantSelected() {
              this.isLoading = true;
              this.toggleSubmitButton(true);
            },

            /**
             * Called when updated variant HTML has been fetched. Updates the
             * active variant ID and clears the loading state.
             * @param {{ variant: object|null }} param0 - The resolved variant object.
             */
            onVariantUpdated({ variant }) {
              // Update the variant ID.
              if (variant) {
                this.variantId = variant.id;
              }

              // @todo: Update the accelerated button state.
              // @todo: Update quantity selector min/max/step for the new variant.

              // Update the loading state.
              this.isLoading = false;
            },

            /**
             * Enables or disables the submit button.
             * @param {boolean} disabled - True to disable, false to enable.
             */
            toggleSubmitButton(disabled) {
              const button = this.submitButton;

              if (button) {
                button.disabled = disabled;
              }
            },

            /**
             * Returns the submit button inside the `addToCart` ref.
             * @returns {HTMLButtonElement|null}
             */
            get submitButton() {
              return this.$refs.addToCart.querySelector("button");
            },

            /**
             * Handles form submission. Collects the variant ID, quantity, and
             * line item properties from the form, then calls `$ajaxCart.add`.
             * Dispatches `adding-to-cart` before the request to allow other
             * components to append additional line items.
             */
            onSubmit() {
              if (timeout) clearTimeout(timeout);

              this.isLoading = true;

              const button = this.submitButton;

              // Return early if the button is already disabled.
              if (button && button.disabled) return;

              // Locate the form element.
              const form =
                this.$el.querySelector("form.product-form") || button.form;

              if (!form) {
                throw new Error("Product form element missing.");
              }

              button.disabled = true;

              const items = [];
              const elements = Array.from(form.elements);

              const variantId =
                elements.find((node) => node.name === "id")?.value ||
                this.variantId;

              const quantity =
                elements.find((node) => node.name === "quantity")?.value || 1;

              const properties =
                elements
                  .filter((node) => node.name.startsWith("properties"))
                  ?.map((node) => ({
                    key: node.name.match(/\[(.*?)\]/)[1],
                    value: node.value,
                  }))
                  .reduce((properties, { key, value }) => {
                    properties[key] = value;
                    return properties;
                  }, {}) || {};

              items.push({
                id: variantId,
                quantity,
                properties,
              });

              // Allow other components to modify the items before adding to cart.
              this.$dispatch("adding-to-cart", {
                items,
                elements,
                quantity,
              });

              this.$ajaxCart.add(
                { items },
                {
                  lastCallback: () => {
                    this.isLoading = false;
                    this.toggleSubmitButton(false);
                  },
                  info: {
                    initiator: this.$root.querySelector(
                      "ajax-cart-product-form",
                    ),
                  },
                },
              );
            },
          };
        },
      };
    });
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive("product-form", (el, { expression }, { evaluate }) => {
      const options = expression ? evaluate(expression) : {};
      root(Alpine, el, options);
    }).before("bind");
  });
})();
